<script lang="ts">
  import { onMount, createEventDispatcher } from 'svelte';
  import type { GameOption, LoadingTextItem, BrandImage } from './types';
  import BrandHeader from './BrandHeader.svelte';
  import { darkenColor, lightenColor, hexToRgb } from './utils';
  import { playProgressTick, playLoadingComplete } from './sounds';

  export let selectedOption: GameOption;
  export let loadingText: LoadingTextItem[];
  export let loadingDurationMs: number;
  export let image: BrandImage;
  export let cardTitle: string | undefined = undefined;

  const dispatch = createEventDispatcher<{ complete: void }>();

  let progress = 0;
  let currentTextIndex = 0;
  let textVisible = true;
  let showContent = false;

  $: currentText = loadingText[currentTextIndex]?.text || 'Loading...';

  let root: HTMLElement;

  onMount(() => {
    // Trigger entrance animation
    setTimeout(() => {
      showContent = true;
    }, 50);

    // Guard against division by zero
    const textCount = loadingText.length || 1;
    const textInterval = loadingDurationMs / textCount;
    const startTime = Date.now();
    let lastTextIndex = 0;
    let lastTickProgress = 0;

    // Track timeouts for cleanup
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      progress = Math.min((elapsed / loadingDurationMs) * 100, 100);
      const newTextIndex = Math.min(
        Math.floor(elapsed / textInterval),
        Math.max(loadingText.length - 1, 0)
      );

      // Play tick sound every 10% progress
      if (Math.floor(progress / 10) > Math.floor(lastTickProgress / 10)) {
        playProgressTick();
        lastTickProgress = progress;
      }

      // Fade text transition with sound
      if (newTextIndex !== lastTextIndex && loadingText.length > 0) {
        textVisible = false;
        playProgressTick();
        timeouts.push(
          setTimeout(() => {
            currentTextIndex = newTextIndex;
            lastTextIndex = newTextIndex;
            textVisible = true;
          }, 150)
        );
      }

      if (elapsed >= loadingDurationMs) {
        clearInterval(progressInterval);
        playLoadingComplete();
        window?.triggerNativeInteractionEvent?.(root, 'loading_complete', {
          option: selectedOption.title
        });
        dispatch('complete');
      }
    }, 50);

    return () => {
      clearInterval(progressInterval);
      timeouts.forEach((t) => clearTimeout(t));
    };
  });
</script>

<div
  class="loading-screen"
  style="--bg-color: {selectedOption.backgroundHex}; --bg-color-dark: {darkenColor(selectedOption.backgroundHex, 25)}; --bg-color-light: {lightenColor(selectedOption.backgroundHex, 15)}; --bg-color-rgb: {hexToRgb(selectedOption.backgroundHex)};"
  bind:this={root}
>
  <!-- Background pattern with screen blend mode -->
  <div class="bg-pattern" aria-hidden="true"></div>

  <BrandHeader {image} {cardTitle} />

  <div class="loading-content" class:show={showContent}>
    <div class="selected-game">
      <span class="game-emoji">{selectedOption.emoji}</span>
      <span class="game-title">{selectedOption.title}</span>
    </div>

    <div class="progress-container">
      <div
        class="progress-bar"
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Loading progress"
      >
        <div class="progress-fill" style="width: {progress}%">
          <div class="progress-shine"></div>
        </div>
      </div>
      <span class="progress-percent">{Math.round(progress)}%</span>
    </div>

    <p class="loading-text" class:visible={textVisible} aria-live="polite">{currentText}</p>

    <div class="loading-dots" aria-hidden="true">
      <span class="dot"></span>
      <span class="dot"></span>
      <span class="dot"></span>
    </div>
  </div>
</div>

<style>
  .loading-screen {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 16px 24px;
    box-sizing: border-box;
    background: linear-gradient(135deg, var(--bg-color) 0%, var(--bg-color-dark) 100%);
    position: relative;
    overflow: hidden;
    container-type: size;
    container-name: loading-screen;
  }

  .bg-pattern {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: 25% 25%;
    background-repeat: no-repeat;
    mix-blend-mode: screen;
    opacity: 0.12;
    pointer-events: none;
  }

  /* Animated background gradient */
  .loading-screen::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at center,
      var(--bg-color-light) 0%,
      transparent 50%
    );
    animation: bgPulse 3s ease-in-out infinite;
    opacity: 0.3;
    pointer-events: none;
  }

  @keyframes bgPulse {
    0%, 100% {
      transform: scale(0.8);
      opacity: 0.2;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.4;
    }
  }

  .loading-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
    max-width: 400px;
    position: relative;
    z-index: 1;
  }

  /* Staggered entrance animations */
  .loading-content .selected-game {
    opacity: 0;
    transform: translateY(15px) scale(0.9);
    transition: opacity 0.4s ease 0.05s, transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.05s;
  }

  .loading-content .progress-container {
    opacity: 0;
    transform: translateY(15px);
    transition: opacity 0.4s ease 0.15s, transform 0.4s ease 0.15s;
  }

  .loading-content .loading-dots {
    opacity: 0;
    transform: translateY(10px);
    transition: opacity 0.3s ease 0.3s, transform 0.3s ease 0.3s;
  }

  .loading-content.show .selected-game {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  .loading-content.show .progress-container {
    opacity: 1;
    transform: translateY(0);
  }

  .loading-content.show .loading-dots {
    opacity: 1;
    transform: translateY(0);
  }

  .selected-game {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border-radius: 6.4px;
  }

  .game-emoji {
    font-size: 24px;
    animation: emojiPulse 1.5s ease-in-out infinite;
  }

  @keyframes emojiPulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }

  .game-title {
    font-size: 17.6px;
    font-weight: 600;
    color: white;
    width: max-content;
  }

  .progress-container {
    width: 100%;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .progress-bar {
    width: 100%;
    height: 6px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 100px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: white;
    border-radius: 100px;
    transition: width 0.1s linear;
    position: relative;
    overflow: hidden;
  }

  .progress-shine {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, transparent 100%);
  }

  .progress-percent {
    font-size: 12.8px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    font-variant-numeric: tabular-nums;
  }

  .loading-text {
    margin: 0;
    font-size: 16px;
    color: white;
    text-align: center;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    opacity: 0;
    transform: translateY(5px);
    transition: opacity 0.15s ease, transform 0.15s ease;
  }

  .loading-text.visible {
    opacity: 0.95;
    transform: translateY(0);
  }

  .loading-dots {
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .dot {
    width: 12px;
    height: 12px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.7) 100%);
    animation: morphDot 2.4s ease-in-out infinite;
    box-shadow: 0 0 12px rgba(255, 255, 255, 0.6);
  }

  .dot:nth-child(1) {
    animation-delay: 0s;
  }

  .dot:nth-child(2) {
    animation-delay: 0.3s;
  }

  .dot:nth-child(3) {
    animation-delay: 0.6s;
  }

  @keyframes morphDot {
    0%, 100% {
      /* Circle */
      border-radius: 50%;
      transform: scale(1) rotate(0deg);
      opacity: 0.6;
      box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
    }
    25% {
      /* Square */
      border-radius: 3px;
      transform: scale(1.1) rotate(0deg);
      opacity: 1;
      box-shadow: 0 0 20px rgba(255, 255, 255, 0.8);
    }
    50% {
      /* Diamond */
      border-radius: 3px;
      transform: scale(1) rotate(45deg);
      opacity: 0.8;
      box-shadow: 0 0 15px rgba(255, 255, 255, 0.7);
    }
    75% {
      /* Square again */
      border-radius: 3px;
      transform: scale(1.1) rotate(45deg);
      opacity: 1;
      box-shadow: 0 0 20px rgba(255, 255, 255, 0.8);
    }
  }

  /* Wide layout adjustments */
  @container (min-aspect-ratio: 3/1) {
    .selected-game {
      padding: 6.4px 12.8px;
    }

    .game-emoji {
      font-size: 20px;
    }

    .game-title {
      font-size: 14.4px;
    }

    .progress-bar {
      height: 4px;
    }

    .progress-percent {
      font-size: 11.200000000000001px;
    }

    .loading-dots {
      gap: 12px;
    }

    .dot {
      width: 8px;
      height: 8px;
    }
  }

  /* Ultra-short wide layout (height <= 150px, width >= 800px) - compact multi-row */
  @container loading-screen (min-aspect-ratio: 3/1) {
    .loading-screen {
      gap: 0;
      padding: 0 24px;
    }

    .loading-content {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      max-width: none;
      padding: 0 24px;
      box-sizing: border-box;
    }

    .selected-game {
      padding: 2px 8px;
    }

    .game-emoji {
      font-size: 16px;
    }

    .game-title {
      font-size: 13px;
    }

    .progress-container {
      gap: 3px;
      padding: 0 40px;
    }

    .progress-bar {
      height: 4px;
    }

    .progress-percent {
      font-size: 10px;
    }

    .loading-text {
      font-size: 12px;
    }

    .loading-dots {
      gap: 10px;
    }

    .dot {
      width: 7px;
      height: 7px;
    }
  }
</style>
